import type { DashboardField, Folder } from "../types/contract";
import { computeActionFlags, type RxCoverageLike } from "./actionFlags";
import { dismissKey, loadDismissals } from "./flagDismissals";

// The dropdown went away but its capability did not: typing "lime" or "soil map" still narrows to
// the fields carrying that flag. Folding it into the search means the flags are matched on the same
// words the user can SEE in the row — the pill's own label — rather than on an internal key they'd
// have to learn.

/* * Flag labels currently showing on a field's row — dismissed ones are gone from the row, so gone here. */
export function activeFlagLabels(
  field: DashboardField,
  year: number,
  coverage: RxCoverageLike[] | null,
): string[] {
  const dismissed = loadDismissals(field.field_id);
  return computeActionFlags(field, coverage, year)
    .filter((f) => !dismissed.has(dismissKey(f.key, year)))
    .map((f) => f.label);
}

/* * Everything about a field a user might reasonably type. */
export function fieldSearchText(
  field: DashboardField,
  flagLabels: string[],
): string {
  return [
    field.field_name ?? "",
    field.acres == null ? "" : String(field.acres),
    ...flagLabels,
  ]
    .join(" ")
    .toLowerCase();
}

export function searchTokens(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

function tokensIn(hay: string, tokens: string[]): boolean {
  return tokens.every((token) => hay.includes(token));
}

// Root first. A missing or cyclic id stops the walk so a stale folder_id cannot loop.
export function folderPathNames(
  folderId: string | null,
  folders: readonly Folder[],
): string[] {
  if (!folderId) return [];
  const byId = new Map(folders.map((folder) => [folder.folder_id, folder]));
  const names: string[] = [];
  const seen = new Set<string>();
  let current: string | null = folderId;
  while (current && !seen.has(current)) {
    seen.add(current);
    const folder = byId.get(current);
    if (!folder) break;
    names.push(folder.name);
    current = folder.parent_folder_id;
  }
  names.reverse();
  return names;
}

/*
 * Does this field match the query? Empty query matches everything (an empty box is not a filter).
 * Every whitespace-separated token must appear in the field text or the folder path — AND, not OR,
 * so adding a word always narrows.
 */
export function fieldMatchesQuery(
  field: DashboardField,
  flagLabels: string[],
  query: string,
  folderNames: readonly string[] = [],
): boolean {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return true;
  const hay = [fieldSearchText(field, flagLabels), ...folderNames]
    .join(" ")
    .toLowerCase();
  return tokensIn(hay, tokens);
}

// One folder name matches only when every token sits in that name. A token in a
// parent and a token in a child is a field-path match, not a folder-name hit.
export function folderNameMatchesQuery(name: string, query: string): boolean {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return false;
  return tokensIn(name.toLowerCase(), tokens);
}

export type FolderSearchHit = {
  // Matched folder, its descendants, and the ancestors needed to see it.
  visibleIds: Set<string>;
  // Matched folder and every folder under it: show every field, not only name hits.
  includeAllIds: Set<string>;
};

function childrenByParent(
  folders: readonly Folder[],
): Map<string | null, string[]> {
  const known = new Set(folders.map((folder) => folder.folder_id));
  const children = new Map<string | null, string[]>();
  for (const folder of folders) {
    const parent =
      folder.parent_folder_id && known.has(folder.parent_folder_id)
        ? folder.parent_folder_id
        : null;
    const list = children.get(parent) ?? [];
    list.push(folder.folder_id);
    children.set(parent, list);
  }
  return children;
}

function collectDescendants(
  id: string,
  children: Map<string | null, string[]>,
  into: Set<string>,
): void {
  into.add(id);
  for (const child of children.get(id) ?? [])
    collectDescendants(child, children, into);
}

// Null when the query matches no folder name. Callers then keep the flat field list.
export function folderSearch(
  folders: readonly Folder[],
  query: string,
): FolderSearchHit | null {
  if (searchTokens(query).length === 0) return null;
  const matched = folders.filter((folder) =>
    folderNameMatchesQuery(folder.name, query),
  );
  if (matched.length === 0) return null;
  const byId = new Map(folders.map((folder) => [folder.folder_id, folder]));
  const children = childrenByParent(folders);
  const visibleIds = new Set<string>();
  const includeAllIds = new Set<string>();
  for (const folder of matched) {
    const descendants = new Set<string>();
    collectDescendants(folder.folder_id, children, descendants);
    for (const id of descendants) {
      visibleIds.add(id);
      includeAllIds.add(id);
    }
    let parent = folder.parent_folder_id;
    const seen = new Set<string>();
    while (parent && byId.has(parent) && !seen.has(parent)) {
      seen.add(parent);
      visibleIds.add(parent);
      parent = byId.get(parent)?.parent_folder_id ?? null;
    }
  }
  return { visibleIds, includeAllIds };
}

// A folder-name hit keeps the tree. A field kept by its own name in a folder the
// hit does not cover must show under that folder (and its ancestors), never as a
// rootless row that reads as Unfiled.
export function searchOpenFolderIds(
  hit: FolderSearchHit | null,
  keptFolderIds: Iterable<string | null>,
  folders: readonly Folder[],
): Set<string> | null {
  if (!hit) return null;
  const byId = new Map(folders.map((folder) => [folder.folder_id, folder]));
  const open = new Set(hit.visibleIds);
  for (const start of keptFolderIds) {
    const seen = new Set<string>();
    let id = start;
    while (id && byId.has(id) && !seen.has(id)) {
      seen.add(id);
      open.add(id);
      id = byId.get(id)?.parent_folder_id ?? null;
    }
  }
  return open;
}

export function searchKeepsField(
  field: DashboardField,
  flagLabels: string[],
  query: string,
  folders: readonly Folder[],
  hit: FolderSearchHit | null,
): boolean {
  const names = folderPathNames(field.folder_id, folders);
  if (fieldMatchesQuery(field, flagLabels, query, names)) return true;
  return (
    field.folder_id != null &&
    (hit?.includeAllIds.has(field.folder_id) ?? false)
  );
}

// Search opens matching folders in the view only. The stored collapse set stays
// so clearing the query puts the tree back where the user left it.
export function collapseIgnoringSearch(
  collapsed: Set<string>,
  openIds: ReadonlySet<string> | null,
): Set<string> {
  if (!openIds || openIds.size === 0) return collapsed;
  const next = new Set(collapsed);
  let changed = false;
  for (const id of openIds) {
    if (next.delete(id)) changed = true;
  }
  return changed ? next : collapsed;
}
